import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { BookMarked, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSeriesTracking } from '@/hooks/useSeriesTracking';

interface SeriesContextBannerProps {
  bookId: string;
  seriesName?: string;
  seriesPosition?: number;
  seriesTotal?: number;
  authors?: string[];
}

export function SeriesContextBanner({
  bookId,
  seriesName,
  seriesPosition,
  seriesTotal,
  authors,
}: SeriesContextBannerProps) {
  const { allSeries } = useSeriesTracking();

  if (!seriesName || !seriesPosition || !authors?.length) {
    return null;
  }

  const primaryAuthor = authors[0];
  const normalizedSeries = seriesName.toLowerCase().trim();

  // Find if user is tracking this series
  const trackedSeries = allSeries.find(
    (s) =>
      s.series_name === normalizedSeries &&
      s.author_name === primaryAuthor &&
      !s.is_dismissed
  );

  if (!trackedSeries) {
    return null;
  }

  const progressPercent = trackedSeries.total_books_known
    ? Math.round((trackedSeries.highest_book_position / trackedSeries.total_books_known) * 100)
    : 0;

  const isCurrentBookRead = trackedSeries.highest_book_position >= seriesPosition;
  const booksRemaining = trackedSeries.total_books_known
    ? trackedSeries.total_books_known - trackedSeries.highest_book_position
    : null;

  return (
    <Card className="card-elevated border-primary/20 bg-primary/5">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-primary/10 shrink-0">
            <BookMarked className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0 space-y-2">
            <div>
              <p className="text-sm text-muted-foreground">
                Part of your series
              </p>
              <p className="font-semibold line-clamp-1">
                {seriesName} (Book {seriesPosition}
                {seriesTotal ? ` of ${seriesTotal}` : ''})
              </p>
              <p className="text-sm text-muted-foreground">
                by {primaryAuthor}
              </p>
            </div>

            {trackedSeries.total_books_known && (
              <div className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Your Progress</span>
                  <span className="font-medium">
                    {trackedSeries.highest_book_position} of {trackedSeries.total_books_known} books
                  </span>
                </div>
                <Progress value={progressPercent} className="h-2" />
                {booksRemaining !== null && booksRemaining > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {booksRemaining} {booksRemaining === 1 ? 'book' : 'books'} remaining
                  </p>
                )}
              </div>
            )}

            {isCurrentBookRead && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                ✓ You've already read this book
              </p>
            )}
          </div>

          <Button variant="outline" size="sm" asChild className="shrink-0">
            <Link to="/series-tracker" className="flex items-center gap-1">
              View Series
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
